import {Button, LinkButton, Tooltip, type ButtonProps} from '@primer/react'

import type {ConflictMergeConditionPayload} from '../../../page-data/payloads/merge-box'
import {ResolveConflictsAgentMenu, type ConflictResolutionAction} from './ResolveConflictsAgentMenu'
import type {AgentMergeConflictResolutionOptions} from '@github-ui/copilot_agent_merge/hooks/use-agent-merge-conflict-resolution'
import type {ConflictsSectionStatuses} from '../../../types'

/**
 *
 * Renders the "Resolve conflicts" link button and handles the inactive state if the viewer cannot resolve conflicts in the web.
 */
export function ResolveConflictsButton({
  conflictsWebEditorPath,
  conflictsState,
  buttonId,
  onError,
  onAgentMergeError,
  onActionSelect,
  onFixWithCopilot,
  agentMerge,
  showAgentOption,
  webEditorConflictResolution,
  size,
}: {
  conflictsWebEditorPath: string
  conflictsState: ConflictsSectionStatuses
  buttonId?: string
  onError?: (message: string) => void
  onAgentMergeError?: (message: string | null) => void
  onActionSelect?: (actionName: ConflictResolutionAction) => void
  onFixWithCopilot?: () => void
  agentMerge?: AgentMergeConflictResolutionOptions
  showAgentOption: boolean
  webEditorConflictResolution: ConflictMergeConditionPayload['webEditorConflictResolution']
  size?: ButtonProps['size']
}) {
  if (conflictsState !== 'HAS_CONFLICTS') return null
  if (!webEditorConflictResolution) return null

  if (
    !webEditorConflictResolution.viewerCanResolve &&
    (!showAgentOption || webEditorConflictResolution.viewerCannotResolve?.reason !== 'TOO_COMPLEX')
  ) {
    return (
      <Tooltip direction="nw" text={webEditorConflictResolution.viewerCannotResolve?.message || ''}>
        <Button inactive size={size}>
          Resolve conflicts
        </Button>
      </Tooltip>
    )
  }

  if (showAgentOption) {
    return (
      <ResolveConflictsAgentMenu
        buttonId={buttonId}
        conflictsWebEditorPath={conflictsWebEditorPath}
        onError={onError}
        onAgentMergeError={onAgentMergeError}
        onActionSelect={onActionSelect}
        onFixWithCopilot={onFixWithCopilot}
        agentMerge={agentMerge}
        webEditorConflictResolution={webEditorConflictResolution}
        size={size}
      />
    )
  }

  return (
    <LinkButton href={conflictsWebEditorPath} size={size}>
      Resolve conflicts
    </LinkButton>
  )
}
